import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';

import { findDsnDefect } from '../build-env';

import {
  IGNORED_ERRORS,
  buildBeforeSend,
  buildDeliveryGateProcessor,
} from './sentry-config';
import { notifySentryReportingChanged } from './sentry-delivery-gate';

/**
 * The React Router bindings `reactRouterBrowserTracingIntegration` needs to name
 * transactions by route pattern (`/organization/:org_id/…`) instead of raw URL.
 *
 * Typed from the SDK and handed in by the app, so this package takes no
 * dependency on `react-router`. PSTUDIO-4288.
 */
export type SentryReactRouterBindings = Pick<
  Parameters<typeof Sentry.reactRouterBrowserTracingIntegration>[0],
  | 'useEffect'
  | 'useLocation'
  | 'useNavigationType'
  | 'createRoutesFromChildren'
  | 'matchRoutes'
>;

/**
 * `Sentry.wrapCreateBrowserRouter` from the SAME `@sentry/react` instance that
 * `sentryInit` installs the React Router integration on.
 *
 * The router instrumentation keeps the bindings in module state, so a wrapper
 * from another copy of the package sees none and silently returns the plain
 * factory. Pro Studio resolves its own nested `@sentry/react` (a hoisting
 * artifact, same version), so its routes stayed raw URLs even with the
 * integration installed — apps must wrap with this export, never their own
 * `Sentry` import. PSTUDIO-4288.
 */
export const sentryWrapCreateBrowserRouter = Sentry.wrapCreateBrowserRouter;

/**
 * The React Router integration opens the page load span itself and passes no
 * start time, so it would start when `Sentry.init` runs — after i18n and the
 * LaunchDarkly gate — and every page load would read shorter than it was, and
 * shorter than before this integration. `browserTracingIntegration()` starts it
 * at the time origin; this restores that. Navigations are left alone.
 */
const startPageLoadAtTimeOrigin = <
  T extends { op?: string; startTime?: unknown },
>(
  spanOptions: T
): T =>
  spanOptions.op === 'pageload' &&
  spanOptions.startTime === undefined &&
  typeof performance !== 'undefined' &&
  performance.timeOrigin
    ? { ...spanOptions, startTime: performance.timeOrigin / 1000 }
    : spanOptions;

export interface SentryInitOptions {
  /**
   * The deploy this bundle is running on: `production`, `uat`, `qa1`,
   * `candidate`, `main` or `preview`.
   *
   * Passed in by the app rather than read here, because this package is consumed
   * as source and bundled by each app's Vite config — so a `process.env` read in
   * this file resolves against the app's `define`/`envPrefix`, not against the
   * deploy. That is exactly how it broke: the value was read from
   * `process.env.ANALYTICS_CAPTURE_ENVIRONMENT`, which no app declared, and every
   * event Sentry received carried `environment: null`.
   */
  environment?: string;
  /** Share of transactions sampled for performance. 0 disables tracing. */
  tracesSampleRate?: number;
  /**
   * Whether this deploy reports to Sentry at all.
   *
   * REQUIRED, and deliberately not defaulted. It replaces the list of
   * environment names this file used to carry, and the decision now belongs to
   * the caller because the answer comes from LaunchDarkly — see
   * `initSentryWithLaunchDarklyGate` in `sentry-gate.ts`, which is what both
   * apps call. A default here would let a new caller report from everywhere by
   * simply not knowing the option exists.
   *
   * `false` skips `Sentry.init` outright rather than filtering events on a
   * client that exists. That is about money, not style: a running client keeps
   * a transport, a session and `tracesSampleRate` sampling alive, while an
   * uninitialised one costs no network at all and makes `reportError` degrade
   * to a no-op on its own. The kill switch below is the other half — it is for
   * a decision that arrives AFTER the client was created.
   */
  enabled: boolean;
  /**
   * Why it is off, for the console line the skip prints.
   *
   * Free text rather than an enum: the only reader is a developer asking why
   * this deploy is quiet, and the useful answer names the flag key, which this
   * package must not hardcode.
   */
  disabledReason?: string;
  /**
   * Router bindings for route-pattern transaction names. Without them tracing
   * falls back to `browserTracingIntegration()` and names by raw URL.
   *
   * Installing the integration is only half of it: `Sentry.wrapCreateBrowserRouter`
   * reads these bindings WHEN IT WRAPS, so the app must create (and wrap) its
   * router after this init has run — see `initSentryWithLaunchDarklyGate`'s
   * returned promise.
   */
  reactRouter?: SentryReactRouterBindings;
}

/**
 * Stands in for a missing environment so an event still groups by deploy.
 *
 * `Sentry.init({ environment: undefined })` produces events with no environment
 * at all, which is indistinguishable from the bug this replaced — an explicit
 * marker is searchable and shows up as a gap to fix.
 *
 * Sentry reaches this fallback again, because nothing here filters by name any
 * more: which deploys report is decided in LaunchDarkly (`sentry-gate.ts`), and
 * a deploy whose flag is on but whose `ANALYTICS_CAPTURE_ENVIRONMENT` is unset
 * does initialise a client. Tagging those events `unknown` is what keeps
 * "forgot the variable" visible in Sentry instead of grouping them under no
 * environment at all. Its other consumer is unchanged — both apps pass it to
 * PostHog as `'Pypestream Environment'` in `main.tsx`.
 */
export const UNKNOWN_ENVIRONMENT = 'unknown';

/**
 * PostHog "Product Data" dataset the Sentry link writes into.
 *
 * https://pypestream.atlassian.net/wiki/spaces/analytics/pages/3632398424/Analytics+Dogfooding+-+Implementation+Details#Sentry-Integration
 */
const POSTHOG_ORGANIZATION = 'pypestream';
const POSTHOG_DATASET_ID = 21;

export const sentryInit = (dsn: string, options: SentryInitOptions) => {
  // The gate decides FIRST, and the skip is not routed through the catch below:
  // on a deploy that was never going to send anything, a missing DSN is not a
  // misconfiguration worth a console error.
  if (!options.enabled) {
    // Announced, never silent — otherwise "no events from this deploy" reads
    // exactly like "Sentry is broken". The caller says WHY it is off, because
    // this function no longer knows: `sentry-gate.ts` passes the flag key and
    // where the answer came from.
    console.log(
      `Sentry: disabled on "${options.environment || UNKNOWN_ENVIRONMENT}"${
        options.disabledReason ? ` — ${options.disabledReason}` : ''
      }`
    );

    return;
  }

  try {
    if (!dsn) {
      throw new Error('Sentry DSN is not provided');
    }

    // PSTUDIO-4260. A non-empty value that is not a DSN used to reach
    // `Sentry.init`, which refuses it internally and leaves a dead client —
    // UAT reported nothing for five months and nobody saw a line about it.
    // The predicate is SHARED with the build-time guard rather than restated,
    // so the two cannot drift into disagreeing about what a DSN is; the build
    // guard is the first line and this is the one that catches every path it
    // cannot reach (a docker build arg, a local `.env.local`, an app the guard
    // is not wired into). The message names the defect, never the value.
    const defect = findDsnDefect(dsn);

    if (defect !== null) {
      throw new Error(`Sentry DSN is unusable — ${defect}`);
    }

    Sentry.init({
      dsn,
      // The fallback is load-bearing again. While this file filtered by name, a
      // deploy that set nothing was refused outright, so no event could carry a
      // null environment. Now the flag decides and such a deploy DOES report —
      // and `Sentry.init({ environment: undefined })` produces events with no
      // environment at all, which is exactly the state PSTUDIO-4190 was about:
      // 4241 events in 30 days that production could not be told apart from a
      // PR preview in. An explicit `unknown` is searchable and reads as a gap
      // to fix.
      environment: options.environment || UNKNOWN_ENVIRONMENT,
      integrations: [
        posthog.sentryIntegration({
          organization: POSTHOG_ORGANIZATION,
          projectId: POSTHOG_DATASET_ID,
        }),
        options.reactRouter
          ? Sentry.reactRouterBrowserTracingIntegration({
              ...options.reactRouter,
              beforeStartSpan: startPageLoadAtTimeOrigin,
            })
          : Sentry.browserTracingIntegration(),
      ],
      // Session Replay is deliberately NOT enabled here. It was switched on once
      // (6319e3978e) and removed five days later by 3d1ec3c807, which deleted the
      // integration together with the `sentryReplay?.start()` call that gated it
      // on consent — the commit it removed carried the comment "Start replay
      // manually on consent". Turning it back on is a privacy decision, not a
      // configuration one: `Sentry.init` runs at DOMContentLoaded, long before
      // `optionalConsentStatus` arrives over GraphQL, and `Sentry.close()`
      // flushes the queue before it disables the client. Re-enable it only
      // behind a consent gate, and only after the author of that removal agrees.
      tracesSampleRate: options.tracesSampleRate ?? 0.1,
      ignoreErrors: IGNORED_ERRORS,
      beforeSend: buildBeforeSend(),
    });
    // The kill switch goes on as a processor, not as a `beforeSend` hook, and
    // AFTER init because it attaches to the client that call just created.
    // `buildDeliveryGateProcessor` explains why the hooks cannot carry it: they
    // are dispatched per event type, and the bug report travels as a third type
    // that neither of them sees.
    Sentry.addEventProcessor(buildDeliveryGateProcessor());
    // The client exists now, and it did not a moment ago. Anything gated on
    // "would a report reach Sentry" — the nav's Report a bug button — has
    // already rendered by this point, because the gate waits for LaunchDarkly
    // before getting here.
    notifySentryReportingChanged();
    console.log(
      '%cSentry: service initialized successfully',
      'color: green; font-size: larger; font-weight: bold'
    );
  } catch (error) {
    console.error('Sentry initialization failed:', error);
  }
};
